<template>
  <div
    v-if="actions.length"
    class="action-buttons fit row no-wrap content-stretch q-gutter-x-sm items-center"
    style="min-width: fit-content"
  >
    <new-request-button
      :label="$t('dashboard.new_request.title')"
      :avoid-modules="[enums.modules.CAT]"
      size="xs"
      btn-classes="re-sm"
      dense
    />
    <re-button
      size="xs"
      stretch
      dense
      :color="firstAction.color"
      :icon="firstAction.icon"
      :target="firstAction.target"
      :disable="!!firstAction.disable || firstAction.loading"
      :href="firstAction.href || undefined"
      :to="firstAction.to || undefined"
      :loading="firstAction.loading"
      :aria-label="firstAction.label"
      btn-classes="q-px-md re-sm"
      :label="$t(`monolith.action-buttons.${firstAction.label}`)"
      @click.stop="firstAction.click ? firstAction.click() : undefined"
    />
    <re-button
      v-if="actions.length > 1"
      size="xs"
      dense
      btn-classes="re-sm"
      icon-right="mdi-dots-vertical"
      style="overflow: auto"
      @click.stop
    >
      <q-menu fit>
        <q-list>
          <q-item
            v-for="(item, index) in actions.slice(1)"
            :key="index"
            v-close-popup
            dense
            :class="`text-${item.color}`"
            :target="item.target"
            :disable="!!item.disable || item.loading"
            :aria-label="item.label"
            :to="item.to || undefined"
            :href="item.href || undefined"
            clickable
            @click.stop="() => (item.click ? item.click() : undefined)"
          >
            <q-item-section avatar>
              <q-spinner
                v-if="item.loading"
                name="mdi-loading"
                size="16px"
                class="q-ml-xs"
              />

              <re-icon
                v-else-if="item.icon"
                :name="item.icon"
                :color="item.color"
                size="20px"
                class="q-ml-xs"
              />
            </q-item-section>

            <q-item-section
              no-wrap
              :text-color="item.color"
            >
              {{ $t(`monolith.action-buttons.${item.label}`) }}
            </q-item-section>
          </q-item>
        </q-list>
      </q-menu>
    </re-button>
    <re-dialog
      v-model="isOwnershipDialogOpen"
      has-cancel
      @confirm="requestMutation.updateOwner()"
    >
      {{ $t('monolith.dashboard.sub_modal.content') }}
    </re-dialog>
    <re-dialog
      v-model="confirmDialog.isOpen"
      has-cancel
      has-confirm
      has-close-button
      header-icon="mdi-exclamation"
      :title="$t('monolith.global.confirm')"
      :type="enums.feedbackType.NEGATIVE"
      @confirm="confirmDialog.onConfirm && confirmDialog.onConfirm()"
    >
      <strong>
        {{ confirmDialog.message }}
      </strong>
    </re-dialog>
  </div>
</template>

<script>
import { useQuasar } from 'quasar'
import { inject } from 'vue'
import { useRoute } from 'vue-router'

import { ersSteps } from '@/apps/ERS/routes'
import NewRequestButton from '@/apps/MainApp/components/NewRequestButton.vue'
import { useCurUser } from '@/composables/useCurUser'
import useReport from '@/composables/useReport'
import { useRequest } from '@/composables/useRequest'
import { actionButtonsKey, requestListKey } from '@/keys'
import enums from '@/plugins/enums.js'

export default {
  name: 'ActionButtons',
  components: {
    NewRequestButton,
  },
  inject: {
    actionButtons: { from: actionButtonsKey, default: null },
  },
  props: {
    row: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const route = useRoute()
    const queryForRefetch =
      route.query.valuation_request_ref !== props.row.valuation_request_ref
        ? inject(requestListKey)
        : undefined // use context curRequest

    const $q = useQuasar()
    const report = useReport({ requestRef: props.row.valuation_request_ref, queryForRefetch })
    const floodReport = useReport({
      requestRef: props.row.valuation_request_ref,
      queryForRefetch,
      purpose: enums.documentPurpose.FLOOD_REPORT,
    })

    // we need to use hijackFilter because skipHijack is not reactive
    $q.loadingBar.setDefaults({
      hijackFilter: () => true,
    })

    return { report, floodReport, curUser: useCurUser() }
  },
  data() {
    const requestMutation = useRequest({
      requestRefInit: this.row.valuation_request_ref,
      startDisabled: true,
    })
    return {
      requestMutation,
      now: Date.now(),
      disabledValuationDownloads: false,
      disabledValuationGeneration: false,
      disabledFloodDownloads: false,
      disabledFloodGeneration: false,
      ersEditLoading: false,
      isOwnershipDialogOpen: false,
      confirmDialog: {
        message: null,
        type: enums.feedbackType.NEGATIVE,
        isOpen: null,
        onConfirm: null,
      },
      enums: enums,
    }
  },
  computed: {
    hasRole() {
      return this.curUser.hasRole
    },
    isAssignedValuer() {
      return this.row.valuer?.is_self
    },
    appointmentStatus() {
      return this.row.latest_appointment?.status
    },
    isOwner() {
      return this.row.owner?.is_self
    },
    isBorrower() {
      return this.row.borrower?.is_self
    },
    hasBeenSubmittedOnce() {
      return this.row.first_submission_at !== null
    },
    actions() {
      const actions = [
        ...this.actionButtons.map((actionFn) => actionFn.call(this)).flat(),

        {
          label: 'cancel',
          color: enums.feedbackType.NEGATIVE,
          icon: 'mdi-cancel',
          // FIXME: use `can.cancel`
          show: this.row.can_cancel,
          loading: this.requestMutation.isBusy,
          click: () => this.cancelSubmission(),
        },
      ]

      return actions.filter((a) => {
        return a.show && (!a.valuationTypes || a.valuationTypes.includes(this.row.valuation_type))
      })
    },
    showOwnershipButton() {
      return (
        this.$config.SUBSTITUTE_FEATURE_ENABLED &&
        !this.isOwner &&
        this.hasRole('substitute', this.row.valuation_type)
      )
    },
    firstAction() {
      return this.actions[0]
    },
  },

  unmounted() {
    // we need to use hijackFilter because skipHijack is not reactive
    this.$q.loadingBar.setDefaults({
      hijackFilter: () => false,
    })
  },

  methods: {
    unsubmitRequest() {
      this.confirmDialog.message = this.$t('monolith.shared.confirm-unsubmit-request')
      this.confirmDialog.isOpen = true
      this.confirmDialog.onConfirm = async () => {
        return this.requestMutation.updateStatus(enums.requestAction.UNSUBMIT).then(() => {
          this.$router.push({
            name: 'dashboard',
          })
        })
      }
    },
    ers_edit_draft(valuation_request_ref) {
      this.ersEditLoading = true
      if (this.row.status === enums.requestStatus.DRAFT) {
        const userIsTheOneCompletingTheFlow =
          !this.requestMutation.data?.borrower || this.requestMutation.data?.borrower.is_self

        return this.$router.push({
          name: 'ers.request',
          params: { action: !userIsTheOneCompletingTheFlow ? ersSteps.CUSTOMER_REF : undefined },
          query: { valuation_request_ref },
        })
      }

      if (this.row.status === enums.requestStatus.FAILED) {
        return this.$router.push({
          name: 'ers.request',
          params: { action: 'submit-summary' },
          query: { valuation_request_ref },
        })
      }

      this.ersEditLoading = false
    },
    cancelSubmission() {
      this.confirmDialog.message = this.$t('ers.dashboard.cancel_submission_message')
      this.confirmDialog.isOpen = true
      this.confirmDialog.onConfirm = () => {
        return this.requestMutation.updateStatus(enums.requestAction.CANCEL)
      }
    },
    unsubmitVal() {
      this.confirmDialog.message = this.$t('ers.dashboard.unvalue_message')
      this.confirmDialog.isOpen = true
      this.confirmDialog.onConfirm = () => {
        return this.requestMutation.updateStatus(enums.requestAction.UNSUBMIT_VALUATION)
      }
    },
    showFloodReport(row, variant) {
      return (
        // Always show the flood report is there is one generated
        (row.flood_report_ref && this.$config.REPORT_VISIBLE.includes(`flood_${variant}`)) ||
        // Dispatchers can always see the web flood report
        (this.hasRole('dispatcher', row.request_type) && variant === 'web')
      )
    },
    reportExists(variant) {
      return (
        enums.requestStatus.VALUED === this.row.status &&
        this.$config.REPORT_VISIBLE.includes(`${this.row.valuation_type}_${variant}`)
      )
    },
    showReport(variant) {
      return (
        // Normally, users can only view the report after the valuation is performed, variant determined by env var.
        (this.reportExists(variant) &&
          !!this.row.released_at &&
          // except for DVMs that require owner review and the owner hasn't accepted yet
          (this.row.valuation_type !== enums.modules.DVM ||
            !this.$config.REQUIRE_OWNER_REVIEW_FOR_DVM ||
            this.row.accepted_by_owner_at)) ||
        // Assigned valuer can always view the web report before the valuation is performed
        (enums.requestStatus.SUBMITTED === this.row.status && this.isAssignedValuer && variant === 'web') ||
        // Dispatchers can always see the web report before and after the valuation is performed
        ([enums.requestStatus.SUBMITTED, enums.requestStatus.VALUED].includes(this.row.status) &&
          this.hasRole('dispatcher', this.row.valuation_type) &&
          variant === 'web') ||
        // avms are not valued, they are simply submitted, so we can always view the report
        (enums.requestStatus.SUBMITTED === this.row.status &&
          this.row.valuation_type === 'avm' &&
          this.$config.REPORT_VISIBLE.includes(`${this.row.valuation_type}_${variant}`))
      )
    },
  },
}
</script>
<style lang="scss" scoped>
:deep(.q-item__section--avatar) {
  min-width: 32px;
}

a.q-item {
  color: inherit;
}
</style>
